import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, AvatarRootProps, Badge, Box, Button, Circle, Float, Separator, Heading, HoverCard, HStack, Icon, Link, Portal, Spinner, Text, VStack, chakra } from '@chakra-ui/react';
import { ButtonLink } from '@src/components/sco/ButtonLink';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ScoUser } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { ensureValidHref, scoBackendUrl } from '@src/lib/startups-url';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { LuBriefcase, LuClock, LuLink, LuSparkle, LuTags } from 'react-icons/lu';
type AvatarProps = AvatarRootProps;
export type UserAvatarProps = AvatarProps & {
  avatarProps?: AvatarProps;
  disablePopover?: boolean;
  disablePresence?: boolean;
  /**
   * Force AI badge. When omitted, tool users (slug `startupscom-tool-*`) and
   * known Wil display names are treated as AI automatically.
   */
  isAI?: boolean;
  href?: string;
  noLink?: boolean;
  user?: ScoUser;
  userId?: string;
};

/** Startups.com tool companion users are provisioned with this slug prefix. */
export const isAiToolUser = (user?: Partial<ScoUser> | null): boolean => {
  if (!user) {
    return false;
  }
  if (user.tool) {
    return true;
  }
  if (user.slug?.startsWith('startupscom-tool-')) {
    return true;
  }
  const name = `${user.fullName || ''} ${user.displayName || ''}`.trim();
  return /^wil(\.ai)?$/i.test(name) || /\bwil\.ai\b/i.test(name);
};

/**
 * User-facing name from backend fields.
 * AI tool companions (e.g. firstName "Startups.com" + lastName "Wil.ai") render as lastName only.
 */
export const displayNameForUser = (user?: Partial<ScoUser> | null, isAI?: boolean): string => {
  const ai = isAI ?? isAiToolUser(user);
  if (ai && user?.lastName) {
    return user.lastName;
  }
  return user?.fullName || user?.displayName || '';
};

/** Overlaid "AI" chip for avatars (also usable on raw Avatar.Root). */
export const AiAvatarBadge = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Float offsetX="0.5" offsetY="0.5" placement="bottom-end"><Circle bg="teal.600" boxShadow="2xs" size="1em"><Icon as={LuSparkle} fontSize="0.5em" /></Circle></Float>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const AdminAvatarBadge = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Float display="none" offsetX="0.5" offsetY="0.5" placement="top-end"><Circle bg="orange.500" boxShadow="2xs" size="1em"><chakra.span fontFamily="poppins" fontSize="0.5em" fontWeight="boldest" paddingBottom="0.25em">s</chakra.span></Circle></Float>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const UserAvatar = (props: UserAvatarProps) => {
  const {
    currentUser
  } = useCurrentUser();
  // Prefer explicit userId so callers can override engagement.advisorUser with a founder id.
  const requestedUserId = props.userId || props.user?.userId || undefined;
  const [user, setUser] = useState<ScoUser>(props.user || {} as ScoUser);
  const query = useQuery(gql`
      query ($userId: String!) {
        userById(userId: $userId) {
          id
          ${UserAvatar.fragments.ScoUser()}
        }
      }
    `, {
    skip: !requestedUserId || props.user?.userId === requestedUserId,
    variables: {
      userId: requestedUserId
    }
  });

  // Drop stale state when switching subjects (e.g. founder A → blank-chat founder B).
  useEffect(() => {
    if (props.user && (!requestedUserId || props.user.userId === requestedUserId)) {
      setUser(props.user);
      return;
    }
    if (requestedUserId && user?.userId && user.userId !== requestedUserId) {
      setUser((props.user || {}) as ScoUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when identity changes
  }, [requestedUserId, props.user?.userId]);
  useEffect(() => {
    const next = query.data?.userById;
    if (next && (!requestedUserId || next.userId === requestedUserId)) {
      setUser(next);
    }
  }, [query.data?.userById, requestedUserId]);

  // Never prefer Apollo data or local state for a different user than the one requested.
  const fromQuery = query.data?.userById && (!requestedUserId || query.data.userById.userId === requestedUserId) ? query.data.userById : null;
  const fromProp = props.user && (!requestedUserId || props.user.userId === requestedUserId) ? props.user : null;
  const fromState = user?.userId && (!requestedUserId || user.userId === requestedUserId) ? user : null;
  const resolvedUser = (fromQuery || fromProp || fromState || props.user || user || {}) as ScoUser;
  const isAI = props.isAI ?? isAiToolUser(resolvedUser);
  const isAdmin = resolvedUser?.isAdmin;
  const displayName = displayNameForUser(resolvedUser, isAI);

  // Chakra v3: rings must use outline* (not border). Avatar.Image is 100% of the root,
  // so border sits under the photo and disappears whenever src loads.
  // Docs: https://chakra-ui.com/docs/components/avatar#ring
  const renderedAvatar = <Avatar.Root as={props.noLink ? Box : NextLink} title={displayName || resolvedUser?.fullName || resolvedUser?.displayName} {...!props.noLink ? {
    href: props.href || `/members/${resolvedUser?.userId}`
  } : {}} {...props.avatarProps || {}} {...isAdmin ? {
    outline: '2px solid',
    outlineColor: 'orange.border/75',
    outlineOffset: '0px'
  } : null} {...isAI ? {
    outline: '2px solid',
    outlineColor: 'teal.border/75',
    outlineOffset: '0px'
  } : null}>
      <Avatar.Fallback name={displayName || resolvedUser?.fullName || resolvedUser?.displayName} />
      <Avatar.Image src={resolvedUser?.pictureUrl || null} />
      {isAI ? <AiAvatarBadge /> : isAdmin ? <AdminAvatarBadge /> : null}
      {!props.disablePresence && resolvedUser?.lastActiveAt && moment.duration(moment().diff(moment(resolvedUser.lastActiveAt))).asMinutes() < 1 && <Float offsetX={'0.5'} offsetY={'0.5'} placement={'bottom-end'}>
            <Circle bg={'green.500'} boxShadow={'2xs'} size={'0.7em'} />
          </Float>}
    </Avatar.Root>;
  if (props.disablePopover) {
    return <Box>{renderedAvatar}</Box>;
  }

  // HoverCard (not Popover): opens on hover. Portal avoids clipping by overflow parents.
  // Pattern matches MentionElement mini-profile.
  return <HoverCard.Root closeDelay={250} lazyMount={true} openDelay={150} positioning={{
    placement: 'right-start',
    gutter: 8
  }}>
      <HoverCard.Trigger asChild>
        <Box cursor={'pointer'} display={'inline-flex'}>
          {renderedAvatar}
        </Box>
      </HoverCard.Trigger>
      <Portal>
        <HoverCard.Positioner>
          <HoverCard.Content borderRadius={'xl'} boxShadow={'lg'} maxWidth={'400px'} p={0} width={'auto'}>
            <Box fontSize={'14px'} minWidth={'220px'} padding={'1em'} textAlign={'left'}>
              {query.loading ? <Spinner /> : <VStack align={'stretch'}>
                  {isAI && <Badge colorPalette={'orange'} display={'block'} padding={2} textAlign={'center'} variant={'subtle'} width={'full'}>
                      AI Tool
                    </Badge>}
                  {isAdmin && <Badge colorPalette={'orange'} display={'block'} padding={2} textAlign={'center'} variant={'subtle'} width={'full'}>
                      Startups.com Team Member
                    </Badge>}
                  <Box>
                    <Heading fontSize={'1.2em'}>
                      {displayName || resolvedUser.fullName || resolvedUser.displayName}
                    </Heading>
                  </Box>
                  {resolvedUser.company?.name && <HStack align={'flex-start'}>
                      <Icon as={LuBriefcase} marginTop={'4px'} width={'1em'} />
                      <Box>
                        <Text fontWeight={900}>
                          <strong>{resolvedUser.company?.name || '-'}</strong>
                        </Text>
                      </Box>
                    </HStack>}
                  {resolvedUser.company?.url && <HStack align={'flex-start'}>
                      <Icon as={LuLink} marginTop={'4px'} width={'1em'} />
                      <Text width={'full'}>
                        <Link href={ensureValidHref(resolvedUser.company?.url)} target={'_blank'} variant={'underline'}>
                          {resolvedUser.company?.url || '-'}
                        </Link>
                      </Text>
                    </HStack>}
                  {(resolvedUser.company?.industry?.title || resolvedUser.company?.industryKey) && <HStack align={'flex-start'}>
                      <Icon as={LuTags} marginTop={'4px'} width={'1em'} />
                      <Text width={'full'}>
                        {resolvedUser.company?.industry?.title || resolvedUser.company?.industryKey}
                      </Text>
                    </HStack>}
                  {resolvedUser.lastActiveAt && !isAI && <HStack align={'flex-start'}>
                      <Icon as={LuClock} marginTop={'4px'} width={'1em'} />
                      <Text width={'full'}>
                        {'Last seen '}
                        {moment(resolvedUser.lastActiveAt).fromNow()}
                      </Text>
                    </HStack>}
                  <Separator />
                  {props.noLink ? <Button size={'sm'} onClick={() => {
                if (resolvedUser.userId) {
                  window.open(`/members/${resolvedUser.userId}`, '_blank', 'noopener,noreferrer');
                }
              }}>
                      View Profile
                    </Button> : <ButtonLink href={`/members/${resolvedUser.userId}`} size={'sm'} target={'_blank'}>
                      View Profile
                    </ButtonLink>}
                  {currentUser?.isAdmin && currentUser?.userToken && resolvedUser?.userToken && (props.noLink ? <Button bgColor={'red'} size={'sm'} onClick={() => {
                window.open(scoBackendUrl('/auth/as-user', {
                  authUserToken: currentUser.userToken,
                  userToken: resolvedUser.userToken
                }), '_blank', 'noopener,noreferrer');
              }}>
                        Login as User
                      </Button> : <ButtonLink bgColor={'red'} size={'sm'} target={'_blank'} href={scoBackendUrl('/auth/as-user', {
                authUserToken: currentUser.userToken,
                userToken: resolvedUser.userToken
              })}>
                        Login as User
                      </ButtonLink>)}
                </VStack>}
            </Box>
          </HoverCard.Content>
        </HoverCard.Positioner>
      </Portal>
    </HoverCard.Root>;
};
UserAvatar.fragments = {
  ScoUser: () => gql`
    fragment _ on ScoUser {
      id
      userId
      userToken
      firstName
      lastName
      fullName
      displayName
      slug
      pictureUrl
      lastActiveAt
      isAdmin
      company {
        id
        name
        url
        logoUrl
        elevatorPitch
        industryKeys
        industryKey
        industry {
          id
          title
        }
      }
      tool {
        id
      }
    }
  `
};